/**
 * As informações do Caso (`TP-072`): o que os documentos validados, os e-mails e os formulários da captação no IW dizem
 * do paciente. O cadastro junta as três fontes e vem primeiro; o resto fica no grupo da fonte. Duas fontes que discordam
 * aparecem lado a lado: a tela não escolhe.
 *
 * Cada valor diz de onde veio, e a fonte abre a origem: o e-mail abre a mensagem na triagem, o anexo e o documento
 * abrem o arquivo. O IW não tem endereço navegável, então diz só o formulário e a data.
 */

import SearchOutlined from '@mui/icons-material/SearchOutlined';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as LinkDeRota } from 'react-router-dom';

import { ErroDaApi } from '../../../shared/api/erros';
import { formatarData, formatarDataHora } from '../../../shared/dominio/datas';
import { urlDoAnexoDaThread } from '../../../shared/triagem/urls';
import { urlDoArquivoDoDocumento } from '../documentos/api';
import { emFrase } from '../documentos/emFrase';
import { ROTULO_DO_CADASTRO } from '../documentos/tipos';
import { informacoesDoCaso } from './api';
import type {
  FonteDaInformacao,
  FormularioDoIw,
  GrupoDaInformacao,
  InformacaoDoCaso,
  LeituraDoIw,
  TipoDaFonte,
} from './tipos';

const TODAS = '';
const ROTULO_DA_SECAO = 'm-0 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[var(--tinta-2)]';
const LINK = 'text-[var(--tinta-2)] underline decoration-[var(--fio)] underline-offset-2 hover:text-[var(--tinta)]';

/** O filtro de fonte: o anexo vem com o e-mail que o trouxe. */
const FILTROS: readonly { chave: string; rotulo: string; tipos: readonly TipoDaFonte[] }[] = [
  { chave: 'documento', rotulo: 'Documentos validados', tipos: ['documento'] },
  { chave: 'email', rotulo: 'E-mails', tipos: ['email', 'anexo'] },
  { chave: 'iw', rotulo: 'IW', tipos: ['iw'] },
];

const FORMULARIO_DO_GRUPO: Partial<Record<GrupoDaInformacao, FormularioDoIw['tipo']>> = {
  iw_avaliacao: 'avaliacao',
  iw_nead: 'nead',
  iw_pad: 'pad',
};

const ORDEM_DOS_GRUPOS: readonly GrupoDaInformacao[] = [
  'documento',
  'email',
  'iw_admissao',
  'iw_avaliacao',
  'iw_nead',
  'iw_pad',
];

function nomeDaInformacao(dado: InformacaoDoCaso): string {
  return dado.cadastro === null ? emFrase(dado.rotulo) : ROTULO_DO_CADASTRO[dado.cadastro];
}

/** Sem acento e sem caixa: "endereco" acha "Endereço". */
function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

function tituloDoGrupo(grupo: GrupoDaInformacao, iw: LeituraDoIw): string {
  switch (grupo) {
    case 'documento':
      return 'Documentos validados';
    case 'email':
      return 'E-mails';
    case 'iw_admissao':
      return `IW · admissão ${String(iw.admissaoId ?? '')}`;
    default: {
      const formulario = iw.formularios.find((f) => f.tipo === FORMULARIO_DO_GRUPO[grupo]);
      if (formulario === undefined) return 'IW';
      const quando = formulario.data === null ? '' : ` · ${formatarData(formulario.data)}`;
      return `IW · ${formulario.template}${quando}${formulario.assinada ? '' : ' · não assinada'}`;
    }
  }
}

function soDasFontes(dado: InformacaoDoCaso, tipos: readonly TipoDaFonte[]): InformacaoDoCaso {
  const valores = dado.valores.filter((v) => v.fontes.some((f) => tipos.includes(f.tipo)));
  const contam = valores.filter((v) => v.fontes.some((f) => !f.aConfirmar));
  return { ...dado, valores, diverge: dado.diverge && contam.length > 1 };
}

function Fonte({ fonte, casoId }: { fonte: FonteDaInformacao; casoId: string }): React.JSX.Element {
  if (fonte.tipo === 'email' && fonte.ticketId !== null && fonte.emailId !== null) {
    const texto = fonte.em === null ? 'e-mail' : `e-mail de ${formatarDataHora(fonte.em)}`;
    return (
      <LinkDeRota
        to={`/fila-de-triagem/${encodeURIComponent(fonte.ticketId)}?mensagem=${encodeURIComponent(fonte.emailId)}`}
        title={fonte.rotulo}
        className={LINK}
      >
        {fonte.tipoDeCuidado === null ? texto : `${fonte.tipoDeCuidado} no ${texto}`}
      </LinkDeRota>
    );
  }
  if (fonte.tipo === 'anexo' && fonte.ticketId !== null && fonte.emailId !== null && fonte.anexoId !== null) {
    return (
      <a href={urlDoAnexoDaThread(fonte.ticketId, fonte.emailId, fonte.anexoId)} title="Baixar o anexo" className={LINK}>
        {fonte.rotulo}
      </a>
    );
  }
  if (fonte.tipo === 'documento' && fonte.documentoId !== null) {
    return (
      <a
        href={urlDoArquivoDoDocumento(casoId, fonte.documentoId)}
        target="_blank"
        rel="noreferrer"
        title={fonte.rotulo}
        className={LINK}
      >
        {fonte.item ?? fonte.rotulo}
      </a>
    );
  }
  return <span>{fonte.tipo === 'iw' ? `IW · ${fonte.rotulo}` : fonte.rotulo}</span>;
}

function Linha({ dado, casoId }: { dado: InformacaoDoCaso; casoId: string }): React.JSX.Element {
  return (
    <div
      className={`grid grid-cols-1 gap-x-6 gap-y-0.5 border-0 border-t border-solid py-2 first:border-t-0 sm:grid-cols-[14rem_1fr] ${
        dado.diverge ? 'border-[var(--sinal-atencao)]' : 'border-[var(--fio)]'
      }`}
    >
      <dt className="text-[0.8125rem] leading-snug text-[var(--tinta-2)]">{nomeDaInformacao(dado)}</dt>
      <dd className="m-0 min-w-0">
        {dado.valores.map((v) => (
          <p key={v.valor} className="m-0 break-words text-[0.875rem] leading-snug text-[var(--tinta)]">
            {v.valor}
            <span className="ml-2 inline-flex flex-wrap gap-x-2 text-[0.75rem] text-[var(--tinta-3)]">
              {v.fontes.map((f) => (
                <Fonte key={`${f.tipo}:${f.documentoId ?? f.anexoId ?? f.emailId ?? f.rotulo}`} fonte={f} casoId={casoId} />
              ))}
              {v.fontes.every((f) => f.aConfirmar) && (
                <span className="font-semibold text-[var(--sinal-atencao)]" title="A máquina leu e ninguém confirmou">
                  a confirmar
                </span>
              )}
            </span>
          </p>
        ))}
        {dado.diverge && (
          <p className="m-0 mt-0.5 text-[0.75rem] font-semibold text-[var(--sinal-atencao)]">As fontes discordam</p>
        )}
      </dd>
    </div>
  );
}

function Lista({ dados, casoId }: { dados: readonly InformacaoDoCaso[]; casoId: string }): React.JSX.Element {
  return (
    <dl className="m-0">
      {dados.map((d) => (
        <Linha key={`${d.grupo}:${d.chave}`} dado={d} casoId={casoId} />
      ))}
    </dl>
  );
}

/** Quantos itens um bloco de e-mail mostra enquanto está recolhido. */
const ITENS_NA_PREVIA = 3;

interface ItemDoBloco {
  readonly nome: string;
  readonly valores: string[];
  readonly diverge: boolean;
}

/** Tudo o que UM e-mail (ou um anexo dele) disse: a fonte aparece uma vez, no cabeçalho do bloco. */
interface BlocoDaFonte {
  readonly chave: string;
  readonly fonte: FonteDaInformacao;
  readonly itens: ItemDoBloco[];
}

function chaveDoBloco(fonte: FonteDaInformacao): string {
  if (fonte.tipo === 'anexo' && fonte.anexoId !== null) return `anexo:${fonte.anexoId}`;
  if (fonte.emailId !== null) return `email:${fonte.emailId}`;
  return `${fonte.tipo}:${fonte.rotulo}`;
}

/** Agrupa as informações do grupo e-mail pela fonte; o mesmo nome dentro do bloco vira um item com vários valores. */
function blocosDoEmail(dados: readonly InformacaoDoCaso[]): BlocoDaFonte[] {
  const blocos = new Map<string, BlocoDaFonte>();
  for (const dado of dados) {
    if (dado.grupo !== 'email') continue;
    const nome = nomeDaInformacao(dado);
    for (const v of dado.valores) {
      for (const f of v.fontes) {
        const chave = chaveDoBloco(f);
        let bloco = blocos.get(chave);
        if (bloco === undefined) {
          bloco = { chave, fonte: f, itens: [] };
          blocos.set(chave, bloco);
        }
        let item = bloco.itens.find((i) => i.nome === nome);
        if (item === undefined) {
          item = { nome, valores: [], diverge: dado.diverge };
          bloco.itens.push(item);
        }
        if (!item.valores.includes(v.valor)) item.valores.push(v.valor);
      }
    }
  }
  return [...blocos.values()];
}

function Bloco({
  bloco,
  total,
  aberto,
  casoId,
}: {
  bloco: BlocoDaFonte;
  total: number;
  /** Com busca ou filtro ativos o bloco abre inteiro: o que bateu não pode ficar escondido. */
  aberto: boolean;
  casoId: string;
}): React.JSX.Element {
  const [expandido, setExpandido] = useState(false);
  const visivel = aberto || expandido;
  const itens = visivel ? bloco.itens : bloco.itens.slice(0, ITENS_NA_PREVIA);
  const contagem = bloco.itens.length === total ? `${String(total)} itens` : `${String(bloco.itens.length)} de ${String(total)}`;
  return (
    <div className="border-0 border-t border-solid border-[var(--fio)] py-2 first:border-t-0">
      <p className="m-0 flex flex-wrap items-baseline gap-x-3 text-[0.8125rem] font-semibold text-[var(--tinta)]">
        <Fonte fonte={bloco.fonte} casoId={casoId} />
        <span className="font-normal tabular-nums text-[var(--tinta-3)]">{contagem}</span>
        {!aberto && total > ITENS_NA_PREVIA && (
          <button
            type="button"
            aria-expanded={expandido}
            onClick={() => {
              setExpandido((atual) => !atual);
            }}
            className={`${LINK} cursor-pointer border-0 bg-transparent p-0 text-[0.75rem] font-normal`}
          >
            {expandido ? 'Recolher' : `Mostrar os ${String(total)}`}
          </button>
        )}
      </p>
      <dl className="m-0 mt-1">
        {itens.map((item) => (
          <div key={item.nome} className="grid grid-cols-1 gap-x-6 gap-y-0.5 py-1 sm:grid-cols-[14rem_1fr]">
            <dt className="text-[0.8125rem] leading-snug text-[var(--tinta-2)]">{item.nome}</dt>
            <dd className="m-0 min-w-0">
              {item.valores.length === 1 ? (
                <p
                  className={`m-0 break-words text-[0.875rem] leading-snug text-[var(--tinta)] ${visivel ? '' : 'line-clamp-3'}`}
                >
                  {item.valores[0]}
                </p>
              ) : (
                <ul className="m-0 list-disc pl-4">
                  {item.valores.map((valor) => (
                    <li
                      key={valor}
                      className={`break-words text-[0.875rem] leading-snug text-[var(--tinta)] ${visivel ? '' : 'line-clamp-3'}`}
                    >
                      {valor}
                    </li>
                  ))}
                </ul>
              )}
              {item.diverge && (
                <p className="m-0 mt-0.5 text-[0.75rem] font-semibold text-[var(--sinal-atencao)]">As fontes discordam</p>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Por que o IW não aparece, ou de que admissão ele veio. */
function AvisoDoIw({ iw }: { iw: LeituraDoIw }): React.JSX.Element | null {
  let texto: string | null = null;
  if (iw.situacao === 'sem_vinculo') {
    texto = 'O paciente ainda não está vinculado ao IW. Os formulários do IW aparecem depois do vínculo.';
  } else if (iw.situacao === 'sem_admissao') {
    texto = 'O paciente não tem admissão no IW.';
  } else if (iw.situacao === 'indisponivel') {
    texto = 'O IW não respondeu agora. O que veio dos documentos e dos e-mails está abaixo.';
  } else if (iw.formularios.length === 0) {
    texto = `A admissão ${String(iw.admissaoId ?? '')} do IW não tem avaliação, NEAD nem PAD.`;
  } else if (iw.admissaoAnteriorAoCaso) {
    texto = `Os formulários do IW são da admissão ${String(iw.admissaoId ?? '')}, anterior a este Caso: ele ainda não abriu a sua no IW.`;
  }
  if (texto === null) return null;
  return (
    <p role="status" className="m-0 text-[0.8125rem] text-[var(--tinta-2)]">
      {texto}
    </p>
  );
}

export function InformacoesDoCaso({ casoId }: { casoId: string }): React.JSX.Element {
  const consulta = useQuery(informacoesDoCaso(casoId));
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState(TODAS);

  if (consulta.isPending) return <p className="m-0 text-[var(--tinta-2)]">Carregando as informações do caso…</p>;
  if (consulta.isError) {
    return (
      <p className="m-0 text-[var(--tinta-2)]">
        {consulta.error instanceof ErroDaApi && consulta.error.status === 503
          ? 'As informações do caso precisam do banco, e ele não está ligado neste ambiente.'
          : 'Não deu para carregar as informações do caso.'}
      </p>
    );
  }

  const { dados, iw } = consulta.data;
  const tipos = FILTROS.find((f) => f.chave === filtro)?.tipos ?? null;

  // O filtro de fonte recorta os VALORES: a informação fica só com o que aquela fonte disse, e só diverge se ainda
  // sobrar mais de um valor na tela.
  const termo = normalizar(busca.trim());
  const filtrados = dados
    .filter(
      (d) =>
        termo === '' ||
        normalizar(`${nomeDaInformacao(d)} ${d.chave} ${d.valores.map((v) => v.valor).join(' ')}`).includes(termo),
    )
    .map((d) =>
      tipos === null
        ? d
        : soDasFontes(d, tipos),
    )
    .filter((d) => d.valores.length > 0);
  const filtrando = termo !== '' || filtro !== TODAS;

  const totalDoBloco = new Map(blocosDoEmail(dados).map((b) => [b.chave, b.itens.length]));
  const cadastro = filtrados.filter((d) => d.grupo === 'cadastro');
  const grupos = ORDEM_DOS_GRUPOS.map((g) => ({ grupo: g, dados: filtrados.filter((d) => d.grupo === g) })).filter(
    (g) => g.dados.length > 0,
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <TextField
          size="small"
          label="Buscar"
          placeholder="cpf, diagnóstico, katz…"
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value);
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ flex: '2 1 14rem', maxWidth: '24rem' }}
        />
        <TextField
          select
          size="small"
          label="Fonte"
          value={filtro}
          onChange={(e) => {
            setFiltro(e.target.value);
          }}
          sx={{ flex: '1 1 12rem', maxWidth: '18rem' }}
        >
          <MenuItem value={TODAS}>Todas as fontes</MenuItem>
          {FILTROS.map((f) => (
            <MenuItem key={f.chave} value={f.chave}>
              {f.rotulo}
            </MenuItem>
          ))}
        </TextField>
        <span className="ml-auto text-[0.75rem] tabular-nums text-[var(--tinta-3)]" aria-live="polite">
          {filtrando ? `${String(filtrados.length)} de ${String(dados.length)}` : `${String(dados.length)} informações`}
        </span>
      </div>

      <AvisoDoIw iw={iw} />

      {dados.length === 0 ? (
        <p className="m-0 text-[var(--tinta-2)]">
          Nada lido ainda. O que os e-mails, os documentos validados e o IW disserem do paciente aparece aqui.
        </p>
      ) : filtrados.length === 0 ? (
        <p className="m-0 text-[0.8125rem] text-[var(--tinta-2)]">Nenhuma informação com esse filtro.</p>
      ) : (
        <>
          {(cadastro.length > 0 || !filtrando) && (
            <section
              aria-label="Cadastro do paciente"
              className="rounded-md border border-solid border-[var(--fio)] bg-[var(--chao-2)] px-4 pb-2 pt-3"
            >
              <p className={ROTULO_DA_SECAO}>Cadastro do paciente</p>
              {cadastro.length === 0 ? (
                <p className="m-0 py-2 text-[0.8125rem] text-[var(--tinta-2)]">Nenhuma fonte traz dado de cadastro.</p>
              ) : (
                <div className="mt-1">
                  <Lista dados={cadastro} casoId={casoId} />
                </div>
              )}
            </section>
          )}
          {grupos.map(({ grupo, dados: doGrupo }) => {
            const titulo = tituloDoGrupo(grupo, iw);
            return (
              <section key={grupo} aria-label={titulo} className="px-1">
                <p className={ROTULO_DA_SECAO}>
                  {titulo} <span className="font-normal tabular-nums text-[var(--tinta-3)]">· {doGrupo.length}</span>
                </p>
                <div className="mt-1">
                  {grupo === 'email' ? (
                    blocosDoEmail(doGrupo).map((bloco) => (
                      <Bloco
                        key={bloco.chave}
                        bloco={bloco}
                        total={totalDoBloco.get(bloco.chave) ?? bloco.itens.length}
                        aberto={filtrando}
                        casoId={casoId}
                      />
                    ))
                  ) : (
                    <Lista dados={doGrupo} casoId={casoId} />
                  )}
                </div>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
